import React, { useEffect, useState } from 'react';

// Components
import { toast } from 'react-toastify';

import { BackdropLoading } from '../../components/BackdropLoading';
import TableModel from '../../components/atoms/tables/TableModel';
import { ShowDialog } from '../../components/ShowDialog';
import { Tooltip } from "@mui/material";
import { IconAction, NewIcon } from './styles';


// Services
import { FormatDate } from '../../utils/formatDate';
import { ListApi } from '../../services/ListApi';
import Router from '../../router';
import { useNavigate } from 'react-router-dom'

// Styles
import { Container, PageTitle, CustomButtonV2 } from '../styles';

// Icons
import { SaveAltRounded, AddCircleOutlineRounded } from '@mui/icons-material';

// Utils
import { CheckResponse } from '../../utils/checkResponse';
import { useTranslation } from 'react-i18next';
import { ColorsContext } from "../../Context/ColorsContext"

//MUI Material 
import { Grid, useTheme, useMediaQuery } from '@mui/material';

// Hook
import { useDownloadExcel } from '../../Hooks/useDownloadExcel';

// Permissions
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../Context/PermissionsContext';
import { SearchBar } from '../../components/atoms/searchBars/SearchBar';

export const Lists = (props) => {
	const { t } = useTranslation();
	const { colors } = React.useContext(ColorsContext);
    const ability = useAbility(AbilityContext);

	const listApi = new ListApi();
	const { check } = new CheckResponse();
	const { format } = new FormatDate();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
	
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
	
    const [filteredRequest, setFilteredRequest] = useState([])
	const [dialog, setDialog] = useState({
		status: false,
		title: '',
		message: '',
		type: null
	})
  
    const [headers, setHeaders] = useState([]);

	//?? Prepare items-table config excel 
	const fieldConfig = {
		[t('lists.table.id')]: 'id',
		[t('lists.table.listName')]: 'name',
		[t('lists.table.integrationKey')]: 'integrationKey',
		[t('lists.table.lastUpdate')]: (item) => format({ date: item.updatedAt, format: true }).toString(),
	};

	//?? CustomHook Download Excel
	const { handleDownloadExcelList } = useDownloadExcel(filteredRequest, fieldConfig, t("lists.title"));


	useEffect(() => {
        returnHeaders();
        
		async function call() {
			await loadData()
		}
		call()
	}, [])

    const returnHeaders = () => {
        let heads = [
            {
                id: 'id',
                label: 'lists.table.id',
                center: false,
                disablePadding: true,
                sortArrow: true,
                isRender: true,
                sticky: true,
                direction: 'left',
            },
            {
                id: 'name',
                label: 'lists.table.listName',
                center: false,
                disablePadding: true,
                sortArrow: true,
                isRender: true,
            },
            {
                id: 'integrationKey',
                label: 'lists.table.integrationKey',
                center: false,
                disablePadding: true,
                sortArrow: true,
                isRender: true,
            },
            {
                id: 'updatedAt',
                label: 'lists.table.lastUpdate',
                center: false,
                disablePadding: true,
                sortArrow: true,
                type: 'date',
            }
        ];
  
        let newActions = [];
        if (ability.can("UpdateList")) {
            newActions.push('edit');
        }
        if (ability.can("DeleteList")) {
            newActions.push('delete');
        }
    
        if (newActions.length > 0) {
            heads.push(addActionsRow(newActions))
        }
    
        setHeaders(heads);
    }
  
    function addActionsRow(actions) {
      return {
            id: 'actions',
            label: 'common.actions.title',
            center: false,
            disablePadding: false,
            sortArrow: false,
            type: 'actions',
            sticky: true,
            direction: 'right',
            keys: actions
        }
    }

	async function loadData() {
		setLoading(true);
		let response = await listApi.list();
		if (response.status === 200) {
			setData(response.data);
		}
		setLoading(false);
		return response.data || [];
	}

	async function handleCreateNew() {
		navigate(Router.appListDetail)
	}

	async function handleEdit(row) {
		navigate(Router.appListDetail, { state: { id: row.id } })
	}

	async function handleDelete(id) {
		setLoading(true)
		let response = await listApi.delete(id);
		let result = check(response)

		if (result.status) {
			setDialog({
				status: true,
				title: t('lists.messages.deletedList.title'),
				message: t('lists.messages.deletedList.message'),
				type: null,
			})
			await loadData();
		}
		else {
			toast.error(() =>
				<div>{t('lists.messages.deletedList.error')}<br />
					{result.errors}
				</div>
			);
		}

		setLoading(false)
	}

	async function handleOpenDialog(row) {
		setDialog({
			status: true,
			title: t('lists.messages.deletedList.confirm'),
			message: t('lists.messages.deletedList.confirmMessage', { list: row.name }),
			type: 'confirmation',
			id: row.id,
		})
	}

	async function handleCloseDialog(value) {
		if (value) {
			setDialog({ ...dialog, status: false })
			await handleDelete(dialog.id);
		}
		else {
			setDialog({ status: false })
		}
	}

	return (
		<Container open={props.open} style={{ marginBottom: 0 }}>
			<Grid container 
				spacing={1}
				alignItems="center"
				sx={{ mb: { md: 1 } }}
				columns={{ xs: 12, sm: 12, md: 12, lg: 23, xl: 15 }}
			>
				<Grid item xs={12} sm={12} md={12} lg={6} xl={4}>
					<PageTitle>
						{t('lists.title')}
					</PageTitle>
				</Grid>
				<Grid item xs={12} sm={6} md={6} lg={8} xl={6}>
					<SearchBar 
						rows={data} 
						setFilteredRequest={setFilteredRequest}
						filterText={t('common.searchBars.lists')}
						filterKeys={['id', 'name', 'integrationKey']}
					/>
				</Grid>
				{isDesktop &&
					<Grid item md={false} lg={1} xl={2}></Grid>
				}
				<Grid item xs={12} sm={6} md={6} lg={8} xl={3}
          			sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>

					{handleCreateNew && (
						<CustomButtonV2
							variant="contained"
							btnType="primary"
							onClick={handleCreateNew}
							startIcon={<AddCircleOutlineRounded />}
						>
							{t("lists.createNew")}
						</CustomButtonV2>
					)}

					<CustomButtonV2
						variant="contained"
						startIcon={<SaveAltRounded />}
						aria-label="download"
						onClick={handleDownloadExcelList}
					>
						{t('common.download')}
					</CustomButtonV2>
				</Grid>
			</Grid>

			<TableModel
                headers={headers}
                filteredRequest={filteredRequest}
                rows={data}
                handleEdit={handleEdit}
                handleDelete={handleOpenDialog}
                isList={true}
                hideToolbar={true}
            />

			<BackdropLoading open={loading} />
			<ShowDialog openDialog={dialog.status} dialog={dialog} handleCloseDialog={handleCloseDialog} />
		</Container >
	)
}
